/**
 * Captured conversations waiting for a person to say who they were with.
 *
 * capture_inbox can be read by its owner but written only by the server, so
 * filing and dismissing go through /api/capture/inbox.
 */

import { pb } from '../lib/pocketbase'
import type { KeyFact } from '../shared/captureFacts'

export type InboxCandidate = {
  entity_type: 'client' | 'prospect' | 'contact'
  entity_id: string
  counterpart: 'self' | 'parent' | 'guardian' | 'advisor' | null
  label: string | null
  name: string | null
}

export type InboxItem = {
  id: string
  source: 'imessage' | 'plaud'
  source_id: string
  occurred_at: string
  last_message_at: string
  summary: string | null
  key_facts: KeyFact[]
  /** Why it could not be filed: a number on two records, or a group spanning two. */
  reason: 'ambiguous_identifier' | 'multiple_records' | 'no_match' | null
  message_count: number | null
  candidates: InboxCandidate[]
}

async function call(body?: Record<string, unknown>) {
  const res = await fetch('/api/capture/inbox', {
    method: body ? 'POST' : 'GET',
    headers: {
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      Authorization: `Bearer ${pb.authStore.token}`,
    },
    body: body ? JSON.stringify(body) : undefined,
  })
  const text = await res.text()
  let data: Record<string, any>
  try {
    data = JSON.parse(text)
  } catch {
    throw new Error(
      `The capture inbox returned ${res.status} but not JSON. If you are running the dev server on its own, this endpoint only exists under \`vercel dev\`.`,
    )
  }
  if (!res.ok) throw new Error(data.error || `Could not reach the capture inbox (${res.status}).`)
  return data
}

export async function listCaptureInbox(): Promise<InboxItem[]> {
  const data = await call()
  if (!Array.isArray(data.items)) throw new Error('The capture inbox did not return a list.')
  return data.items as InboxItem[]
}

export async function fileCaptureInboxItem(inboxId: string, target: { entity_type: InboxCandidate['entity_type']; entity_id: string }) {
  return call({ action: 'file', inbox_id: inboxId, ...target }) as Promise<{ activity_id: string; stamped: boolean; already?: boolean }>
}

export async function dismissCaptureInboxItem(inboxId: string) {
  return call({ action: 'dismiss', inbox_id: inboxId }) as Promise<{ id: string; dismissed_at: string }>
}
